import React, { useContext, useCallback } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import PropTypes from 'prop-types';
import CoverageTermsComponent from '../CoverageTermsComponent/CoverageTermsComponent';
import metadata from './CoveragesTableComponent.metadata.json5';
import styles from './CoveragesTableComponent.module.scss';
import messages from './CoveragesTableComponent.messages';
import appConfig from 'app-config';
import { IntlContext } from '@jutro/legacy/locale';

import { BreakpointTrackerContext } from '@jutro/legacy/layout';

const DEFAULT_COV_PREMIUM = {
    amount: 0.00,
    currency: 'usd'
};

function CoveragesTableComponent(props) {
    const intl = useContext(IntlContext);
    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext)
    const isMobile = ['phone', 'phoneWide'].includes(breakpoint);
    const isQnB = appConfig.portalCode === 'QB';
    const isQBmobileUI = isMobile & isQnB

    const {
        model: vehicle,
        displayedLineCoverages,
        isModernProduct
    } = props;

    const primaryUseCode =_.get(vehicle, 'primaryUse.value.code', '');

    const coverages = (() => {
    const vehicleCategory = _.get(vehicle, 'category_ML.value.code');
    const allCoverages = _.get(vehicle, 'coverages', []);

    let result = allCoverages;

    if (vehicleCategory === 'camper_np' || vehicleCategory === 'trailer_np') {
        const allowedCoverages = [
            'PAContentFireCov_Ext',
            'PATrailerCamperCov_Ext',
            'PATowandRoadServCov_EXT',
            'PAComprehensiveCov',
            'PACollisionCov'
        ];

        result = result.filter((cov) =>
            allowedCoverages.includes(cov.code_ML)
        );
    }

    result = result.map((cov) => {
        if (cov?.name === 'Non-Owner') {
            return {
                ...cov,
                name: isModernProduct ? 'Non Owner' : 'Named Non-Owner'
            };
        }
        return cov;
    });

    if (primaryUseCode === 'storage_np') {
        const allowedCoveragesForStorage = [
            'PAComprehensiveCov',
            'PACollisionCov',
            'PADecliningDeductibleCov_Ext',
            'PAZeroGlassDeductibleCov_Ext',
            'PATowandRoadServCov_EXT',
            'PANewOriginalPartsCov_Ext'
        ];

        result = result.filter((cov) =>
            allowedCoveragesForStorage.includes(cov.code_ML)
        );
    }
  
const hasCompOrCollision = allCoverages.some((coverage) => {
  const isCompOrCollision = ["PAComprehensiveCov", "PACollisionCov"].includes(
    coverage.code_ML,
  );
  return isCompOrCollision && coverage.selected === true;
});

if ( hasCompOrCollision===false && isModernProduct ) {
  const dependentCoverageCodes = [
    "PADecliningDeductibleCov_Ext",
    "PALimitedMexicoCov_Ext", 
  ];
  result = result.filter(
    (coverage) => !dependentCoverageCodes.includes(coverage.code_ML),
  );
}
    return result;
})();

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const getCoveragePremium = useCallback((cov) => {
        let vehicleCovPremium = DEFAULT_COV_PREMIUM;
        if (cov && cov.amount && cov.name !== 'Regular Use of Cars Owned by Others') {
            vehicleCovPremium = cov.amount;
        }
        if (cov && cov.vehicleCovCosts_ML) {
            cov.vehicleCovCosts_ML.forEach((vehicleCovCost) => {
                if (vehicleCovCost && vehicleCovCost.vehiclePublicID && vehicleCovCost.vehiclePublicID === vehicle.publicID.value) {
                    vehicleCovPremium = vehicleCovCost.cost;
                }
            });
        }
        return vehicleCovPremium;
    }, [vehicle.publicID]);

    const covAmountDisplayed = useCallback((coverage) => {
        if (displayedLineCoverages.has(coverage.code_ML)) {
            const vehId = displayedLineCoverages.get(coverage.code_ML);
            return vehId !== vehicle.publicID.value;
        }
        return false;
    }, [displayedLineCoverages, vehicle.publicID.value]);

    const getPolicyLevelCostForCoverage = useCallback((coverage) => {
        const isTrailer = vehicle.category_ML === 'trailer';
        const suspendLiabCov = vehicle.coverages.find((cov) => cov.code_ML === 'PASuspendLiabilityCov_ML');
        if (isTrailer || (suspendLiabCov && suspendLiabCov.selected)) {
            return undefined;
        }
        if (!covAmountDisplayed(coverage, vehicle)) {
            displayedLineCoverages.set(coverage.code_ML, vehicle.publicID.value);
            return formatAmountString(coverage.lineCovCost_Ext);
        }
        return 'Included';
    }, [covAmountDisplayed, displayedLineCoverages, formatAmountString, vehicle]);

    const getCoverageTerms = (cov) => {
        let covTerms = [];
        if (cov && cov.allCoverageTerms_ML && cov.allCoverageTerms_ML.length) {
            covTerms = cov.allCoverageTerms_ML;
        } else if (cov && cov.terms && cov.terms.length) {
            covTerms = cov.terms;
        }
        return covTerms.filter((term) => term.isVisible_ML === true && !term.name.includes("Coverage Is"));
    };

    const displayPremium = useCallback((coverage) => {
        if (!coverage.isLineCovCost_Ext) {
            return formatAmountString(getCoveragePremium(coverage));
        }
        if (vehicle && coverage.isLineCovCost_Ext) {
            return getPolicyLevelCostForCoverage(coverage);
        }
    }, [formatAmountString, getCoveragePremium, getPolicyLevelCostForCoverage, vehicle]);

    const generateOverrides = () => {
        const overrides = coverages.map((coverage, index) => {
            const coverageTerms = getCoverageTerms(coverage);
            return {
                [`coveragesRow${index}`]: {
                    visible: coverage.selected,
                    className: isQBmobileUI ? 'tablerow qnbTableMobile' : 'tablerow'
                },
                [`premium${index}`]: {
                    content: displayPremium(coverage)
                },
                [`coverageTermsComponent${index}`]: {
                    model: coverageTerms,
                    covIndex: index
                },
                [`limitsCell${index}`]: {
                    visible: !isQBmobileUI
                },
            };
        });

        return Object.assign({}, ...overrides);
    };

    const resolvers = {
        resolveComponentMap: {
            coverageTermsComponent: CoverageTermsComponent
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        coveragesIterable: {
            data: coverages
        },
        coverageHeader: {
            content: isQBmobileUI ? translator(messages.summaryCoverageLimit) : translator(messages.summaryCoverage)
        },
        limitsHeader: {
            visible: !isQBmobileUI
        },
        coveragesHeaderRow: {
            className: isQBmobileUI ? 'headerrow qnbTableMobile' : 'headerrow'
        },
        ...generateOverrides()
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

CoveragesTableComponent.propTypes = {
    model: PropTypes.shape({}).isRequired,
    displayedLineCoverages: PropTypes.shape({}).isRequired
};

export default CoveragesTableComponent;
